import { __rest } from 'tslib';
import { cx } from '@emotion/css';
import { Box, Typography, useTheme, useMediaQuery, Stack, Divider } from '@mui/material';
import React, { useMemo, useCallback, Fragment } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { makeG2STyles } from '@komune-io/g2-themes';
import ReactMarkdown from 'react-markdown';
import 'github-markdown-css';
import gfm from 'remark-gfm';
import raw from 'rehype-raw';

var a11yDark = {
  "hljs-comment": {
    "color": "#d4d0ab"
  },
  "hljs-quote": {
    "color": "#d4d0ab"
  },
  "hljs-variable": {
    "color": "#ffa07a"
  },
  "hljs-template-variable": {
    "color": "#ffa07a"
  },
  "hljs-tag": {
    "color": "#ffa07a"
  },
  "hljs-name": {
    "color": "#ffa07a"
  },
  "hljs-selector-id": {
    "color": "#ffa07a"
  },
  "hljs-selector-class": {
    "color": "#ffa07a"
  },
  "hljs-regexp": {
    "color": "#ffa07a"
  },
  "hljs-deletion": {
    "color": "#ffa07a"
  },
  "hljs-number": {
    "color": "#f5ab35"
  },
  "hljs-built_in": {
    "color": "#f5ab35"
  },
  "hljs-builtin-name": {
    "color": "#f5ab35"
  },
  "hljs-literal": {
    "color": "#f5ab35"
  },
  "hljs-type": {
    "color": "#f5ab35"
  },
  "hljs-params": {
    "color": "#f5ab35"
  },
  "hljs-meta": {
    "color": "#f5ab35"
  },
  "hljs-link": {
    "color": "#f5ab35"
  },
  "hljs-attribute": {
    "color": "#ffd700"
  },
  "hljs-string": {
    "color": "#abe338"
  },
  "hljs-symbol": {
    "color": "#abe338"
  },
  "hljs-bullet": {
    "color": "#abe338"
  },
  "hljs-addition": {
    "color": "#abe338"
  },
  "hljs-title": {
    "color": "#00e0e0"
  },
  "hljs-section": {
    "color": "#00e0e0"
  },
  "hljs-keyword": {
    "color": "#dcc6e0"
  },
  "hljs-selector-tag": {
    "color": "#dcc6e0"
  },
  "hljs": {
    "display": "block",
    "overflowX": "auto",
    "background": "#2b2b2b",
    "color": "#f8f8f2",
    "padding": "0.5em"
  },
  "hljs-emphasis": {
    "fontStyle": "italic"
  },
  "hljs-strong": {
    "fontWeight": "bold"
  }
};

var a11yLight = {
  "hljs-comment": {
    "color": "#696969"
  },
  "hljs-quote": {
    "color": "#696969"
  },
  "hljs-variable": {
    "color": "#d91e18"
  },
  "hljs-template-variable": {
    "color": "#d91e18"
  },
  "hljs-tag": {
    "color": "#d91e18"
  },
  "hljs-name": {
    "color": "#d91e18"
  },
  "hljs-selector-id": {
    "color": "#d91e18"
  },
  "hljs-selector-class": {
    "color": "#d91e18"
  },
  "hljs-regexp": {
    "color": "#d91e18"
  },
  "hljs-deletion": {
    "color": "#d91e18"
  },
  "hljs-number": {
    "color": "#aa5d00"
  },
  "hljs-built_in": {
    "color": "#aa5d00"
  },
  "hljs-builtin-name": {
    "color": "#aa5d00"
  },
  "hljs-literal": {
    "color": "#aa5d00"
  },
  "hljs-type": {
    "color": "#aa5d00"
  },
  "hljs-params": {
    "color": "#aa5d00"
  },
  "hljs-meta": {
    "color": "#aa5d00"
  },
  "hljs-link": {
    "color": "#aa5d00"
  },
  "hljs-attribute": {
    "color": "#aa5d00"
  },
  "hljs-string": {
    "color": "#008000"
  },
  "hljs-symbol": {
    "color": "#008000"
  },
  "hljs-bullet": {
    "color": "#008000"
  },
  "hljs-addition": {
    "color": "#008000"
  },
  "hljs-title": {
    "color": "#007faa"
  },
  "hljs-section": {
    "color": "#007faa"
  },
  "hljs-keyword": {
    "color": "#7928a1"
  },
  "hljs-selector-tag": {
    "color": "#7928a1"
  },
  "hljs": {
    "display": "block",
    "overflowX": "auto",
    "background": "#fefefe",
    "color": "#545454",
    "padding": "0.5em"
  },
  "hljs-emphasis": {
    "fontStyle": "italic"
  },
  "hljs-strong": {
    "fontWeight": "bold"
  }
};

var atomOneDark = {
  "hljs": {
    "display": "block",
    "overflowX": "auto",
    "padding": "0.5em",
    "color": "#abb2bf",
    "background": "#282c34"
  },
  "hljs-comment": {
    "color": "#5c6370",
    "fontStyle": "italic"
  },
  "hljs-quote": {
    "color": "#5c6370",
    "fontStyle": "italic"
  },
  "hljs-doctag": {
    "color": "#c678dd"
  },
  "hljs-keyword": {
    "color": "#c678dd"
  },
  "hljs-formula": {
    "color": "#c678dd"
  },
  "hljs-section": {
    "color": "#e06c75"
  },
  "hljs-name": {
    "color": "#e06c75"
  },
  "hljs-selector-tag": {
    "color": "#e06c75"
  },
  "hljs-deletion": {
    "color": "#e06c75"
  },
  "hljs-subst": {
    "color": "#e06c75"
  },
  "hljs-literal": {
    "color": "#56b6c2"
  },
  "hljs-string": {
    "color": "#98c379"
  },
  "hljs-regexp": {
    "color": "#98c379"
  },
  "hljs-addition": {
    "color": "#98c379"
  },
  "hljs-attribute": {
    "color": "#98c379"
  },
  "hljs-meta-string": {
    "color": "#98c379"
  },
  "hljs-built_in": {
    "color": "#e6c07b"
  },
  "hljs-class .hljs-title": {
    "color": "#e6c07b"
  },
  "hljs-attr": {
    "color": "#d19a66"
  },
  "hljs-variable": {
    "color": "#d19a66"
  },
  "hljs-template-variable": {
    "color": "#d19a66"
  },
  "hljs-type": {
    "color": "#d19a66"
  },
  "hljs-selector-class": {
    "color": "#d19a66"
  },
  "hljs-selector-attr": {
    "color": "#d19a66"
  },
  "hljs-selector-pseudo": {
    "color": "#d19a66"
  },
  "hljs-number": {
    "color": "#d19a66"
  },
  "hljs-symbol": {
    "color": "#61aeee"
  },
  "hljs-bullet": {
    "color": "#61aeee"
  },
  "hljs-link": {
    "color": "#61aeee",
    "textDecoration": "underline"
  },
  "hljs-meta": {
    "color": "#61aeee"
  },
  "hljs-selector-id": {
    "color": "#61aeee"
  },
  "hljs-title": {
    "color": "#61aeee"
  },
  "hljs-emphasis": {
    "fontStyle": "italic"
  },
  "hljs-strong": {
    "fontWeight": "bold"
  }
};

var atomOneLight = {
  "hljs": {
    "display": "block",
    "overflowX": "auto",
    "padding": "0.5em",
    "color": "#383a42",
    "background": "#fafafa"
  },
  "hljs-comment": {
    "color": "#a0a1a7",
    "fontStyle": "italic"
  },
  "hljs-quote": {
    "color": "#a0a1a7",
    "fontStyle": "italic"
  },
  "hljs-doctag": {
    "color": "#a626a4"
  },
  "hljs-keyword": {
    "color": "#a626a4"
  },
  "hljs-formula": {
    "color": "#a626a4"
  },
  "hljs-section": {
    "color": "#e45649"
  },
  "hljs-name": {
    "color": "#e45649"
  },
  "hljs-selector-tag": {
    "color": "#e45649"
  },
  "hljs-deletion": {
    "color": "#e45649"
  },
  "hljs-subst": {
    "color": "#e45649"
  },
  "hljs-literal": {
    "color": "#0184bb"
  },
  "hljs-string": {
    "color": "#50a14f"
  },
  "hljs-regexp": {
    "color": "#50a14f"
  },
  "hljs-addition": {
    "color": "#50a14f"
  },
  "hljs-attribute": {
    "color": "#50a14f"
  },
  "hljs-meta-string": {
    "color": "#50a14f"
  },
  "hljs-built_in": {
    "color": "#c18401"
  },
  "hljs-class .hljs-title": {
    "color": "#c18401"
  },
  "hljs-attr": {
    "color": "#986801"
  },
  "hljs-variable": {
    "color": "#986801"
  },
  "hljs-template-variable": {
    "color": "#986801"
  },
  "hljs-type": {
    "color": "#986801"
  },
  "hljs-selector-class": {
    "color": "#986801"
  },
  "hljs-selector-attr": {
    "color": "#986801"
  },
  "hljs-selector-pseudo": {
    "color": "#986801"
  },
  "hljs-number": {
    "color": "#986801"
  },
  "hljs-symbol": {
    "color": "#4078f2"
  },
  "hljs-bullet": {
    "color": "#4078f2"
  },
  "hljs-link": {
    "color": "#4078f2",
    "textDecoration": "underline"
  },
  "hljs-meta": {
    "color": "#4078f2"
  },
  "hljs-selector-id": {
    "color": "#4078f2"
  },
  "hljs-title": {
    "color": "#4078f2"
  },
  "hljs-emphasis": {
    "fontStyle": "italic"
  },
  "hljs-strong": {
    "fontWeight": "bold"
  }
};

const useStyles$1 = makeG2STyles()({
    root: {
        ' span:hover span': {
            filter: 'brightness(1.2)'
        }
    }
});
const HttpDefinitionHighlighter = (props) => {
    const { httpDefinitions } = props, other = __rest(props, ["httpDefinitions"]);
    const { classes } = useStyles$1();
    const body = useMemo(() => {
        return httpDefinitions.map((def) => `${def.method} ${def.path}`).join('\n');
    }, [httpDefinitions]);
    const lineProps = useCallback((lineNumber) => ({
        style: { cursor: 'pointer', display: 'block', marginLeft: 15 },
        onClick() {
            const url = httpDefinitions[lineNumber].url;
            if (url) {
                const link = document.createElement('a');
                link.href = httpDefinitions[lineNumber].url;
                link.click();
            }
        }
    }), [httpDefinitions]);
    return (React.createElement(Box, { className: classes.root },
        React.createElement(SyntaxHighlighter, Object.assign({}, other, { language: 'curl', wrapLines: true, showLineNumbers: true, startingLineNumber: 0, lineNumberStyle: { display: 'none' }, lineProps: lineProps }), body)));
};

const highlightStyleMap = new Map([
    ['atome-one-dark', atomOneDark],
    ['atome-one-light', atomOneLight],
    ['a11y-dark', a11yDark],
    ['a11y-light', a11yLight]
]);
const CodeHighlighter = (props) => {
    const { code, children, object, highlightStyle = 'atome-one-dark', title, style, className, id, language = 'typescript' } = props, other = __rest(props, ["code", "children", "object", "highlightStyle", "title", "style", "className", "id", "language"]);
    const formatedObject = useMemo(() => {
        if (!object)
            return;
        return JSON.stringify(object, undefined, 2);
    }, [object]);
    const selectedStyle = useMemo(() => highlightStyleMap.get(highlightStyle), [highlightStyle]);
    const body = useMemo(() => {
        var _a;
        switch (language) {
            case 'http-definition':
                return (React.createElement(HttpDefinitionHighlighter, Object.assign({}, other, { language: language, style: selectedStyle, httpDefinitions: object })));
            default:
                return (React.createElement(SyntaxHighlighter, Object.assign({}, other, { language: language, style: selectedStyle }), (_a = code !== null && code !== void 0 ? code : formatedObject) !== null && _a !== void 0 ? _a : children));
        }
    }, [language, code, formatedObject, children, selectedStyle]);
    return (React.createElement(Box, { style: style, className: cx('AruiCodeHighlighter-root', className), sx: {
            '& pre': Object.assign({ borderRadius: '8px', border: '1px solid #EEEEEE', margin: 0, padding: '16px !important' }, (title
                ? {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderTop: '0px solid #EEEEEE'
                }
                : {}))
        }, id: id },
        title && (React.createElement(Box, { className: 'AruiCodeHighlighter-titleContainer', sx: {
                width: '100%',
                padding: '8px 16px',
                boxSizing: 'border-box',
                background: '#FEF9EE',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                border: '1px solid #EEEEEE'
            } },
            React.createElement(Typography, { className: 'AruiCodeHighlighter-title', variant: 'subtitle1' }, title))),
        body));
};

const useStyles = makeG2STyles()({
    markdown: {
        fontSize: '14px',
        lineHeight: '22px',
        fontFamily: '"Nunito Sans",-apple-system,".SFNSText-Regular","San Francisco",BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif',
        '& pre': {
            backgroundColor: 'unset',
            padding: '0',
            marginBottom: '8px'
        },
        '& img': {
            width: '100%',
            maxWidth: '300px'
        },
        '& p': {
            marginBottom: '10px'
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '20px',
            marginBottom: '13px',
            fontWeight: 700
        },
        '& h1': {
            fontWeight: 800
        },
        '& hr': {
            height: '.15em',
            margin: '15px 0',
            background: '#b7c0c9',
            borderRadius: '20px'
        },
        '& article': {
            padding: '15px 0',
            borderBottom: 'solid 1px #b7c0c9'
        },
        '& article p:last-of-type': {
            margin: 0
        },
        '& article:last-of-type': {
            borderBottom: 'none'
        },
        '& blockquote': {
            margin: '10px 0'
        },
        '& code': {
            margin: '0 2px',
            padding: '3px 5px',
            borderRadius: '3px',
            border: '1px solid #EEEEEE',
            backgroundColor: '#F8F8F8'
        }
    }
});
const components = {
    //@ts-ignore
    code: (_a) => {
        var { node, inline, className, children } = _a, props = __rest(_a, ["node", "inline", "className", "children"]);
        const match = /language-(\w+)/.exec(className || '');
        return !inline && match ? (React.createElement(CodeHighlighter, Object.assign({ language: match[1], code: String(children).replace(/\n$/, '') }, props))) : (React.createElement("code", Object.assign({ className: className }, props), children));
    },
    mark: (object) => {
        return (React.createElement("mark", { style: { backgroundColor: object.color } }, object.children));
    }
};
const MarkdownHighlighter = (props) => {
    const { markdown, className } = props;
    const { classes, cx } = useStyles();
    return (React.createElement(ReactMarkdown, { className: cx(classes.markdown, 'markdown-body', className), remarkPlugins: [[gfm, { singleTilde: false }]], rehypePlugins: [raw], 
        //@ts-ignore
        components: components }, markdown));
};

const SegmentedContainer = (props) => {
    const { leftElement, rightElement, description, leftSegmentPortion = 0.6, className, style, id } = props;
    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const child = !isSM ? (React.createElement(React.Fragment, null,
        React.createElement(Box, { className: 'AruiSegmentedContainer-leftContainer', sx: {
                width: `calc(${leftSegmentPortion * 100}% - ${50 * (1 - leftSegmentPortion)}px)`
            } },
            description,
            leftElement),
        React.createElement(Box, { className: 'AruiSegmentedContainer-rightContainer', sx: {
                position: 'sticky',
                top: '16px',
                alignSelf: 'flex-start',
                width: `calc(${(1 - leftSegmentPortion) * 100}% - ${50 * leftSegmentPortion}px)`
            } }, rightElement))) : (React.createElement(Box, { className: 'AruiSegmentedContainer-mobileContainer' },
        description,
        rightElement,
        leftElement));
    return (React.createElement(Stack, { className: cx('AruiSegmentedContainer-root', className), style: style, id: id, direction: 'row', gap: '50px' }, child));
};

const DocsTable = (props) => {
    const { gridColumnNumber = 2, children } = props;
    const display = useMemo(() => {
        if (!Array.isArray(children))
            return;
        let dividerCount = 0;
        return children.map((child, index) => {
            const canDisplayDivider = index > 0 &&
                (index + 1) % gridColumnNumber === 0 &&
                index + 1 < children.length;
            if (canDisplayDivider)
                dividerCount++;
            const dividerIndex = (index + 1) / gridColumnNumber + dividerCount;
            return (React.createElement(Fragment, { key: index },
                child,
                canDisplayDivider && (React.createElement(Divider, { sx: {
                        gridArea: {
                            xs: 'unset',
                            sm: `${dividerIndex} / 1 / ${dividerIndex} / ${gridColumnNumber + 1}`
                        },
                        width: 'calc(100% + 48px)',
                        marginLeft: '-24px',
                        borderColor: '#EEEEEE'
                    } }))));
        });
    }, [children, gridColumnNumber]);
    return (React.createElement(Box, { sx: {
            display: 'grid',
            gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: `repeat(${gridColumnNumber}, 1fr)`
            },
            gridColumnGap: '50px',
            gridRowGap: '16px',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid #EEEEEE',
            background: '#FFFEFB',
            alignItems: 'center',
            margin: '8px 0'
        } }, display));
};

export { CodeHighlighter, DocsTable, MarkdownHighlighter, SegmentedContainer };
//# sourceMappingURL=index.es.js.map
